import {useEffect, useState} from 'react';
import { createPortal } from 'react-dom';
import { FileText, X } from 'lucide-react';
import api from "../../utils/axiosInstance";
import { btn } from '../Button/buttonStyles';
import {toast} from "../Toast/toast";

const PdfPreviewModal = ({ title, url, fileName, onClose }) => {
  const [pdf, setPdf] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setLoading(true);
    api
      .get(url, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const blob = new Blob([res.data], { type: 'application/pdf' });
        objectUrl = window.URL.createObjectURL(blob);
        setPdf({blob, url: objectUrl});
      })
        .catch((err) => {
            console.error(err);
            if (!cancelled) toast.error("Failed to load the PDF.");
        })
        .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
    }, [url]);

    useEffect(() => {
        const handleKey = (e) => e.key === "Escape" && onClose(); 
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [onClose]);

    const download = () => {
        if (!pdf) return;
        const a = document.createElement('a');
        a.href = pdf.url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    // Portalled to body so the backdrop also covers the fixed navbar and sidebar.
    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900/40 p-4"
            onMouseDown={(e) => e.target === e.currentTarget && onClose()}
        >
            <div
                role="dialog"
                aria-modal="true"
                className="flex h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-pop"
            >
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
                    <h2 className='text-sm font-semibold text-gray-900'>{title}</h2>
                    <div className="flex items-center gap-2">
                        <button type="button" onClick={download} disabled={!pdf || loading}  className={btn("primary", "sm")}>
                            Download PDF
                        </button>
                        <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label="Close">
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 bg-gray-100">
                    {loading ? (
                        <div className="flex h-full items-center justify-center">
                           <div className="h-8 w-8 animate-spin rounded-full border border-gray-300 border-t-brand-600" /> 
                        </div>
                    ) : pdf ? (
                        <iframe title={fileName} src={pdf.url} className="h-full w-full" />
                    ) : (
                        <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
                            <FileText className="h-8 w-8" />
                            <p className="text-sm">Failed to load the PDF.</p>
                        </div>
                    )}
                </div>

            </div>
        </div>,
        document.body
    );
};

export default PdfPreviewModal;